<template>
  <div>
    <!-- Login Required Alert (for non-logged-in users) -->
    <v-alert
      v-if="!isLoggedIn"
      color="primary"
      prominent
      border="left"
      class="mb-4 login-required-alert"
      dark
    >
      <v-row align="center" no-gutters>
        <v-col cols="auto" class="mr-4">
          <v-icon size="48" color="white">mdi-account-lock</v-icon>
        </v-col>
        <v-col>
          <div class="text-h6 font-weight-bold mb-2">Login Required</div>
          <div class="text-body-1">
            To import bookmarks into LinkBook.io, you need to be logged in.
            Create a free account to get started!
          </div>
        </v-col>
      </v-row>
      <v-row class="mt-4">
        <v-col cols="12" sm="auto" class="pb-2 pb-sm-3">
          <v-btn
            color="white"
            href="/login"
            large
            block
            elevation="2"
            class="primary--text font-weight-bold"
          >
            <v-icon left>mdi-login</v-icon>
            Login
          </v-btn>
        </v-col>
        <v-col cols="12" sm="auto" class="pb-2 pb-sm-3">
          <v-btn
            color="success"
            href="/signup"
            large
            block
            elevation="2"
            class="font-weight-bold"
          >
            <v-icon left>mdi-account-plus</v-icon>
            Sign Up Free
          </v-btn>
        </v-col>
      </v-row>
    </v-alert>

    <!-- Step-by-step instructions -->
    <v-card class="mb-4" elevation="2">
      <v-card-title class="primary white--text">
        <v-icon left color="white">mdi-import</v-icon>
        Import Bookmarks
      </v-card-title>
      <v-card-text class="pt-4">
        <v-timeline dense align-top>
          <v-timeline-item small color="primary">
            <strong>Step 1:</strong> Export bookmarks from your browser as an
            HTML file
          </v-timeline-item>
          <v-timeline-item small color="primary">
            <strong>Step 2:</strong> Upload the HTML file below
          </v-timeline-item>
          <v-timeline-item small color="primary">
            <strong>Step 3:</strong> Wait for processing to complete
          </v-timeline-item>
        </v-timeline>
      </v-card-text>
    </v-card>

    <!-- Import Section -->
    <v-card
      class="mb-4"
      elevation="2"
      :class="{ 'disabled-overlay': !isLoggedIn }"
    >
      <v-card-title class="text-h6">
        <v-icon left>mdi-file-upload</v-icon>
        Upload Multiple Bookmarks
      </v-card-title>
      <v-card-subtitle class="pb-0">
        Import many bookmarks at once from an HTML file
      </v-card-subtitle>
      <v-card-text>
        <v-alert type="info" text outlined dense class="mb-4">
          Supported format: HTML bookmark files (.html or .htm) exported from
          your browser
        </v-alert>

        <v-checkbox
          v-model="overwriteExisting"
          color="primary"
          :disabled="isProcessing || !isLoggedIn"
          class="mt-0"
        >
          <template v-slot:label>
            <div>
              <strong>Overwrite existing bookmarks</strong>
              <div class="text-caption grey--text">
                If enabled, duplicate bookmarks will be replaced with the
                imported version
              </div>
            </div>
          </template>
        </v-checkbox>

        <v-file-input
          v-model="file"
          label="Select HTML bookmark file"
          outlined
          prepend-inner-icon="mdi-file-upload-outline"
          prepend-icon=""
          @change="readFile"
          :rules="fileRule"
          accept=".htm,.html"
          :disabled="isProcessing || !isLoggedIn"
          show-size
          class="mt-4"
          hint="Select the HTML file you exported from your browser"
          persistent-hint
        ></v-file-input>

        <v-alert v-if="feedback" type="error" class="mt-4" dismissible>
          {{ feedback }}
        </v-alert>

        <!-- Processing Status -->
        <v-expand-transition>
          <div v-if="isProcessing">
            <v-divider class="my-4"></v-divider>
            <div class="text-center mb-3">
              <v-icon color="primary" class="rotating">mdi-loading</v-icon>
              <span class="ml-2 text-h6">Processing your bookmarks...</span>
            </div>

            <v-progress-linear
              :value="progressPercentage"
              height="30"
              striped
              color="primary"
              class="mb-3"
            >
              <template v-slot:default="{ value }">
                <strong class="white--text">{{ Math.ceil(value) }}%</strong>
              </template>
            </v-progress-linear>

            <v-card outlined>
              <v-card-text>
                <div class="d-flex justify-space-between align-center">
                  <div>
                    <v-icon left color="primary">mdi-bookmark-multiple</v-icon>
                    <strong>Bookmarks:</strong> {{ processedCount }} /
                    {{ totalBookmarks }}
                  </div>
                  <div>
                    <v-icon left color="primary">mdi-web</v-icon>
                    <strong>Domains:</strong> {{ processedDomains }} /
                    {{ totalUniqueDomains }}
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </v-expand-transition>
      </v-card-text>
    </v-card>

    <!-- Quick Tip -->
    <v-alert type="info" text outlined border="left" class="mb-4">
      <div>
        <strong>Adding just one bookmark?</strong>
        <div class="text-body-2 mt-1">
          Use the <v-icon small>mdi-plus</v-icon> add link button in the bottom
          right corner instead.
        </div>
      </div>
    </v-alert>

    <!-- Export Section -->
    <v-card elevation="2">
      <v-card-title>
        <v-icon left>mdi-export</v-icon>
        Export Your Bookmarks
      </v-card-title>
      <v-card-text>
        <p class="mb-4">
          Already have bookmarks in LinkBook.io? Export them to use with other
          services or as a backup.
        </p>
        <v-btn color="primary" outlined href="/export-bookmarks">
          <v-icon left>mdi-download</v-icon>
          Go to Export Page
        </v-btn>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import api from '../../api'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'ImportBookmark',
  data() {
    return {
      file: null,
      content: null,
      response: null,
      feedback: null,
      fileRule: [
        v => !!v || 'File is required',
        v => (v && v.type === 'text/html') || '.html or .htm file is required'
      ],
      isProcessing: false,
      processedCount: 0,
      totalBookmarks: 0,
      overwriteExisting: false,
      uniqueDomains: new Set(),
      processedDomains: 0,
      totalUniqueDomains: 0
    }
  },
  created() {
    // Only fetch links if user is logged in
    if (this.isLoggedIn) {
      this.fetchLinks()
    }
  },
  computed: {
    ...mapGetters(['getLinks', 'isLoggedIn']),
    progressPercentage() {
      return this.totalUniqueDomains > 0
        ? (this.processedDomains / this.totalUniqueDomains) * 100
        : 0
    }
  },
  methods: {
    ...mapActions(['addLink', 'updateLink', 'deleteLink', 'fetchLinks']),

    async processBatchedBookmarks(bookmarks, parentFolder = null) {
      this.isProcessing = true
      this.isCompleted = false
      this.processedCount = 0
      this.processedDomains = 0
      this.totalBookmarks = this.countBookmarks(bookmarks)

      // Count unique domains
      this.uniqueDomains = new Set(
        bookmarks.map(item => api.extractDomain(item.url || ''))
      )
      this.totalUniqueDomains = this.uniqueDomains.size

      // Fetch existing links before processing
      await this.fetchLinks()

      // Get domains from Vuex store
      const { domains } = this.$store.state.links || {}

      // Create a flat array of all links from all categories
      const existingLinks = Object.values(domains || {}).flat()

      const existingBookmarksMap = new Map(
        existingLinks.map(link => [link.link, link])
      )

      for (const item of bookmarks) {
        await this.processBookmarkItem(item, parentFolder, existingBookmarksMap)
        this.processedCount++
        this.processedDomains++
      }
      this.finishProcessing()
    },

    async processBookmarkItem(item, parentFolder, existingBookmarksMap) {
      if (!item || item.type !== 'link') return

      // Handle empty category
      const category = item.category || parentFolder || 'Uncategorised'
      const linkData = {
        title: item.title || '',
        link: item.url || '',
        category: category,
        categoryTitle: category,
        domain: api.extractDomain(item.url || ''),
        favourite: false
      }

      const existingBookmark = existingBookmarksMap.get(item.url)

      try {
        if (existingBookmark && this.overwriteExisting) {
          await this.deleteLink({ doc_id: existingBookmark.doc_id })
          existingBookmarksMap.delete(item.url)
        }

        if (!existingBookmark || this.overwriteExisting) {
          const addedBookmark = await this.addLink(linkData)
          existingBookmarksMap.set(item.url, addedBookmark)

          // Remove the domain from uniqueDomains as it's been processed
          this.uniqueDomains.delete(linkData.domain)

          // If all unique domains have been processed, finish
          if (this.uniqueDomains.size === 0) {
            this.finishProcessing()
          }
        }
      } catch (error) {
        console.error('Error processing bookmark:', item, error)
      }
    },

    countBookmarks(items) {
      let count = 0
      items.forEach(item => {
        if (item.type === 'link') {
          count++
        } else if (item.type === 'folder' && item.children) {
          count += this.countBookmarks(item.children)
        }
      })
      return count
    },

    readFile() {
      if (!this.file) return

      this.isProcessing = true
      this.feedback = null
      this.processedCount = 0
      this.totalBookmarks = 0
      this.processedDomains = 0
      this.totalUniqueDomains = 0

      const reader = new FileReader()
      if (this.file.name.includes('.html') || this.file.name.includes('.htm')) {
        reader.onload = res => {
          this.content = res.target.result

          // Check for correct document syntax
          if (!this.content.includes('<!DOCTYPE NETSCAPE-Bookmark-file-1>')) {
            this.isProcessing = false
            const errorMessage =
              'The uploaded file does not have the correct bookmark syntax.'
            this.$notify({
              type: 'error',
              group: 'action',
              title: 'Invalid File',
              text: errorMessage
            })

            this.feedback = errorMessage
            return // Prevent further processing
          }

          const bookmarks = this.parseBookmarks(this.content)
          this.processBatchedBookmarks(bookmarks)
        }
        reader.onerror = err => {
          console.log(err)
          this.isProcessing = false
        }
        reader.readAsText(this.file)
      } else {
        this.isProcessing = false
        // Handle invalid file type
      }
    },

    parseBookmarks(content) {
      const parser = new DOMParser()
      const doc = parser.parseFromString(content, 'text/html')
      return this.parseBookmarkFolder(doc.body)
    },

    parseBookmarkFolder(element, parentFolder = '') {
      if (!element) return [] // Return empty array if element is null

      const bookmarks = []
      for (const child of element.children) {
        if (child.tagName === 'DT') {
          const h3 = child.querySelector('H3')
          if (h3) {
            const folderName = h3.textContent
            const dl = child.querySelector('DL')
            const folderBookmarks = dl
              ? this.parseBookmarkFolder(dl, folderName)
              : []
            bookmarks.push(...folderBookmarks)
          } else {
            const a = child.querySelector('A')
            if (a) {
              bookmarks.push({
                type: 'link',
                title: a.textContent || '',
                url: a.href || '',
                category: parentFolder
              })
            }
          }
        } else if (child.tagName === 'DL') {
          // If we encounter a DL directly, parse its contents with the current parentFolder
          bookmarks.push(...this.parseBookmarkFolder(child, parentFolder))
        }
      }
      return bookmarks
    },

    finishProcessing() {
      if (this.isCompleted) return // Prevent multiple calls

      this.isProcessing = false
      this.isCompleted = true
      this.$notify({
        type: 'success',
        group: 'action',
        title: 'Bookmark Import',
        text: `${this.processedCount} bookmarks have been ${
          this.overwriteExisting ? 'imported and overwritten' : 'imported'
        } successfully`
      })

      // Only redirect after a short delay to ensure the user sees the 100% completion
      setTimeout(() => {
        this.$router.push('/categories')
      }, 1000)
    }
  }
}
</script>

<style scoped>
@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.rotating {
  animation: rotate 2s linear infinite;
}

/* Fix for v-file-input not opening file picker */
::v-deep .v-file-input input[type='file'] {
  pointer-events: auto !important;
}

/* Disabled overlay for non-logged-in users */
.disabled-overlay {
  position: relative;
}

.disabled-overlay::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  pointer-events: none;
  z-index: 1;
}

/* Login Required Alert Styling */
.login-required-alert {
  border-left-width: 8px !important;
}

.login-required-alert .v-icon {
  opacity: 1 !important;
}
</style>
